import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const normalizeMeterDetails = (value) => {
  if (typeof value === "string") return value.trim() ? [{ type: "", serial: value, mpan: "", mprn: "", read: "" }] : [];
  if (!Array.isArray(value)) return [];
  return value.map((meter) => typeof meter === "string"
    ? { type: "", serial: meter, mpan: "", mprn: "", read: "" }
    : { type: meter?.type || "", serial: meter?.serial || "", mpan: meter?.mpan || "", mprn: meter?.mprn || "", read: meter?.read || "", photo_file_id: meter?.photo_file_id || null });
};

export const cleanMeterDetails = (value) => normalizeMeterDetails(value)
  .filter((meter) => meter.type && meter.serial.trim())
  .map((meter) => ({ type: meter.type, serial: meter.serial.trim(), mpan: meter.mpan.trim() || null, mprn: meter.mprn.trim() || null, read: meter.read.trim() || null, photo_file_id: meter.photo_file_id || null }));

export const hasMeterDetails = (value) => normalizeMeterDetails(value)
  .some((meter) => meter.type || meter.serial.trim() || meter.mpan.trim() || meter.mprn.trim() || meter.read.trim());

export const hasIncompleteMeterDetails = (value) => normalizeMeterDetails(value)
  .some((meter) => (meter.type || meter.serial.trim() || meter.mpan.trim() || meter.mprn.trim() || meter.read.trim()) && (!meter.type || !meter.serial.trim()));

export const MeterDetailsFields = ({ value = [], onChange, testId }) => {
  const meters = normalizeMeterDetails(value);
  const update = (index, changes) => onChange(meters.map((meter, meterIndex) => meterIndex === index ? { ...meter, ...changes } : meter));

  return (
    <div className="space-y-3" data-testid={`${testId}-meters`}>
      {meters.map((meter, index) => (
        <div key={index} className="grid gap-2 sm:grid-cols-2 xl:grid-cols-[minmax(110px,0.7fr)_minmax(130px,1.3fr)_minmax(110px,1fr)_minmax(110px,1fr)_minmax(90px,0.8fr)_40px] xl:items-end" data-testid={`${testId}-meter-${index}`}>
          <div>
            <Label htmlFor={`${testId}-meter-type-${index}`}>Meter {index + 1} type</Label>
            <Select value={meter.type || "unset"} onValueChange={(type) => update(index, { type: type === "unset" ? "" : type })}>
              <SelectTrigger id={`${testId}-meter-type-${index}`} className="mt-1" data-testid={`${testId}-meter-type-${index}`}><SelectValue placeholder="Choose type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">Choose type</SelectItem>
                <SelectItem value="Electric">Electric</SelectItem>
                <SelectItem value="Gas">Gas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor={`${testId}-meter-serial-${index}`}>Serial</Label>
            <Input id={`${testId}-meter-serial-${index}`} value={meter.serial} onChange={(event) => update(index, { serial: event.target.value })} maxLength={100} className="mt-1" data-testid={`${testId}-meter-serial-${index}`} />
          </div>
          <div>
            <Label htmlFor={`${testId}-meter-mpan-${index}`}>MPAN</Label>
            <Input id={`${testId}-meter-mpan-${index}`} value={meter.mpan} onChange={(event) => update(index, { mpan: event.target.value })} maxLength={30} className="mt-1" data-testid={`${testId}-meter-mpan-${index}`} />
          </div>
          <div>
            <Label htmlFor={`${testId}-meter-mprn-${index}`}>MPRN</Label>
            <Input id={`${testId}-meter-mprn-${index}`} value={meter.mprn} onChange={(event) => update(index, { mprn: event.target.value })} maxLength={30} className="mt-1" data-testid={`${testId}-meter-mprn-${index}`} />
          </div>
          <div>
            <Label htmlFor={`${testId}-meter-read-${index}`}>Read</Label>
            <Input id={`${testId}-meter-read-${index}`} value={meter.read} onChange={(event) => update(index, { read: event.target.value })} maxLength={30} className="mt-1" data-testid={`${testId}-meter-read-${index}`} />
          </div>
          <Button type="button" size="icon" variant="ghost" className="text-rose-700" aria-label={`Remove meter ${index + 1}`} title={`Remove meter ${index + 1}`} onClick={() => onChange(meters.filter((_, meterIndex) => meterIndex !== index))} data-testid={`${testId}-meter-remove-${index}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" disabled={meters.length >= 20} onClick={() => onChange([...meters, { type: "", serial: "", mpan: "", mprn: "", read: "" }])} data-testid={`${testId}-meter-add`}>
        <Plus className="mr-1 h-4 w-4" />Add meter
      </Button>
    </div>
  );
};